import React, { useState, useRef, useCallback, useContext, useEffect } from "react";
import { CustomError } from "../../../utils/customeError";
import { ErrorCallback, ProgressCallback, SKMediaType, SuccessCallback, uploadFile } from "../../../api/shared/uploadService";
import { UploadTask } from "firebase/storage";
import AudioPlayer from "../../../components/AudioPlayer";
import * as Yup from "yup";
import { ContentState } from "../../../models/SKArtifact";
import { CreateArtifactContext } from "../../../context/CreateArtifactContext";
import { isEditable } from "@testing-library/user-event/dist/utils";

interface AudioInputProps {
  onFileChange?: (url: string | null) => void;
  audioUrl?: string;
  maxAudioDuration?: number;
  maxFileSize?: number;
  docId: string;
  editable?: boolean;
  removeAudio?: () => void;
  folder: string;
}

const AudioInput = ({
  docId,
  onFileChange,
  removeAudio,
  audioUrl,
  maxAudioDuration = 300,
  maxFileSize = 4,
  editable = true,
  folder,
}: AudioInputProps) => {
  // const { contentData, artifactId, updateContentData, isEditable } = useContext(CreateArtifactContext);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [videoTask, setVideoTask] = useState<UploadTask | null>(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [divHeight, setDivHeight] = useState<number>(150);
  const [divWidth, setDivWidth] = useState<number>(150);

  const progressCallback: ProgressCallback = (progress: number, mediaType: SKMediaType) => {
    console.log(`Upload progress: ${progress}%`);
  };

  const errorCallback: ErrorCallback = (error: Error, mediaType: SKMediaType) => {
    console.error(`Upload failed: ${error.message}`);
    setUploadingAudio(false);
  };

  const fileChange = (url: string) => {
    onFileChange && onFileChange(url);
  };

  const successCallback: SuccessCallback = (url: string, mediaType: SKMediaType) => {
    setUploadingAudio(false);
    fileChange(url);
  };

  const audioSchema = Yup.object().shape({
    audioFile: Yup.mixed<File>()
      .test("fileSize", "The file is too large", value => {
        return value && value.size <= maxFileSize * 1024 * 1024;
      })
      .test("fileDuration", "The file duration is too long", async value => {
        if (!value) return false;
        const fileDuration = await new Promise<number>(resolve => {
          const reader = new FileReader();
          reader.onload = (event: any) => {
            const audio = new Audio(event.target.result);
            audio.onloadedmetadata = () => {
              resolve(audio.duration);
            };
          };
          reader.readAsDataURL(value);
        });
        return fileDuration <= maxAudioDuration;
      }),
  });

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      (document.getElementById("file-upload-audio") as HTMLInputElement).value = "";
      if (!file) return;
      try {
        const fileUrl = URL.createObjectURL(file);
        await audioSchema.validate({ audioFile: file }, { abortEarly: false });

        setErrorMessage("");
        setUploadingAudio(true);
        let task = uploadFile(file, docId, SKMediaType.video, progressCallback, errorCallback, successCallback, folder);
        setVideoTask(task);
      } catch (err) {
        let error = err as Error;
        setErrorMessage(error.message);
      }
    },
    [successCallback, docId, audioSchema]
  );

  // const removeAudio = () => {
  //   onFileChange && onFileChange(null);
  // };

  useEffect(() => {
    const aspectRatio = 150 / 150;
    if (divWidth > 290) {
      setDivWidth(290);
      setDivHeight(290 / aspectRatio);
    } else if (divHeight > 150) {
      setDivHeight(150);
      setDivWidth(150 * aspectRatio);
    }
  }, [divWidth, divHeight]);

  return (
    <div className={`flex flex-col w-[${divWidth}]px h-[${divHeight}]px`} style={{ width: divWidth, height: divHeight }}>
      <div
        className="relative w-full h-full image-input" // Ensure this container has relative positioning
        onClick={() => {
          if (editable) {
            fileInputRef?.current?.click();
          }
          //editable && fileInputRef?.current?.click();
        }}
      >
        {!uploadingAudio && removeAudio && audioUrl && editable && (
          <button
            id="clsbtn"
            className="absolute top-2 right-2 bg-white rounded-full p-[1px] shadow-md w-[18px] h-[18px]"
            onClick={e => {
              e.stopPropagation();
              removeAudio && removeAudio();
            }}
          >
            <img src="/icons/CloseButton.png" alt="remove" />
          </button>
        )}
        <input
          name="audioFile"
          type="file"
          ref={fileInputRef}
          accept=".mp3,.m4a,.wav"
          onChange={handleFileChange}
          className="hidden"
          id="file-upload-audio"
          disabled={!editable}
        />

        {uploadingAudio && (
          <div className="flex flex-col items-center justify-center p-4 space-y-4 bg-white rounded-2xl">
            <img src="/icons/mediaupload.gif" alt="Saving" className="w-[25px] h-[25px]" />
            <div className="font-bold text-xs text-center text-gray-400 w-full h-[16px]">Please wait...</div>
            <button
              className="text-black border border-gray-400 rounded-md w-[53px] h-[30px]"
              onClick={e => {
                e.stopPropagation();
                setUploadingAudio(false);
                videoTask && videoTask.cancel();
              }}
            >
              Cancel
            </button>
          </div>
        )}

        {audioUrl && !uploadingAudio && (
          <div className="w-full overflow-hidden rounded-lg mt-[27px]">
            <AudioPlayer src={audioUrl} />
          </div>
        )}
        {!(uploadingAudio || audioUrl) && (
          <label htmlFor="file-upload" className="m-4 cursor-pointer">
            <div className="flex flex-col items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-[27px] h-[30px] mb-[5px] text-[#8E8E93]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
              </svg>
              <span className="image-input-media-type">M4A, MP3, or WAV</span>
            </div>
          </label>
        )}
      </div>
      {errorMessage && <p className="mt-2 text-sm text-red-500">{errorMessage}</p>}
    </div>
  );
};

export default AudioInput;
